"use client"

import * as React from "react"
import { ChevronsUpDown, Building2, Globe } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { useRouter, usePathname } from "next/navigation"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { UserText } from "@/components/ui/user-text"
import { useWorkspaceStore } from "@/stores/workspace-store"
import { useWorkspaces } from "@/hooks/api"
import { useFeatureVisibility } from "@/hooks/use-feature-visibility"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

// Workspace and company names are what a tenant typed, in any script: they
// render through <UserText>, so an Arabic name keeps its first words when the
// rail truncates it.
export function TeamSwitcher() {
  const { isMobile } = useSidebar()
  const queryClient = useQueryClient()
  const router = useRouter()
  const pathname = usePathname()
  const [mounted, setMounted] = React.useState(false)
  const { t } = useShellTranslation()

  React.useEffect(() => {
    setMounted(true)
  }, [])

  // Workspace store state
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace)
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces)
  const showStoreSelector = useWorkspaceStore((s) => s.showWorkspaceSelector)
  const features = useFeatureVisibility()

  // Use SSR-safe values until client hydration completes.
  // workspace-store persists activeWorkspaceId (can be 'all'), so Zustand rehydration
  // can make the client render a <SidebarMenu> while the server rendered null → crash.
  const hydratedWorkspaceId = mounted ? activeWorkspaceId : null
  const hydratedWorkspaces = mounted ? workspaces : []

  const isAllView = hydratedWorkspaceId === 'all'
  // Show selector if store says multiple workspaces OR feature visibility says so (demo mode)
  const showSelector = () => showStoreSelector() || features.showWorkspaceSelector

  // Fetch workspaces from API and sync to store.
  // FIX: 2026-04-19 — do NOT logout on empty data.
  // Root cause: a transient empty `data: []` (network hiccup, backend still booting)
  // was treated identically to a genuine "user has no workspaces", triggering a full
  // session wipe + redirect. 401/403 genuine auth failures are already handled by the
  // client.ts interceptor; we never need to duplicate logout logic here.
  const { data: fetchedWorkspaces, isLoading: workspacesLoading, isSuccess: workspacesSuccess } = useWorkspaces()

  React.useEffect(() => {
    if (workspacesSuccess && fetchedWorkspaces && fetchedWorkspaces.length > 0) {
      // Confirmed non-empty response — sync to store.
      setWorkspaces(fetchedWorkspaces)
    }
    // Empty-data: do nothing. Either the query is still in-flight (show shimmer via
    // workspacesLoading) or it genuinely returned zero workspaces (show empty state below).
    // A real auth failure (401/403) is caught upstream by client.ts and never reaches here.
  }, [fetchedWorkspaces, workspacesSuccess, setWorkspaces])

  const activeWorkspace = isAllView
    ? { id: 'all', name: t('nav.allWorkspaces'), companyId: hydratedWorkspaces[0]?.companyId ?? '', companyName: hydratedWorkspaces[0]?.companyName ?? '' }
    : hydratedWorkspaces.find((w) => w.id === hydratedWorkspaceId)

  const handleSwitch = React.useCallback(
    (workspaceId: string) => {
      if (workspaceId === activeWorkspaceId) return
      setActiveWorkspace(workspaceId)
      // Invalidate all caches so data reloads for the new workspace
      queryClient.invalidateQueries()

      if (workspaceId === 'all') {
        router.push('/company-dashboard')
      } else if (pathname === '/company-dashboard') {
        // Leaving the all-workspaces view — go back to recipients
        router.push('/recipients')
      }
    },
    [activeWorkspaceId, setActiveWorkspace, queryClient, router, pathname],
  )

  // Loading shimmer — workspaces still in-flight; don't show stale or blank UI.
  if (workspacesLoading && hydratedWorkspaces.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled className="animate-pulse opacity-60">
            <div className="bg-sidebar-primary/40 flex aspect-square size-8 items-center justify-center rounded-lg">
              <Building2 className="size-4 opacity-0" />
            </div>
            <div className="grid flex-1 gap-1">
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="h-2 w-16 rounded bg-muted" />
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  // Genuinely zero workspaces (query settled, no data, no auth error).
  // Real auth errors (401/403) are handled upstream by client.ts and redirect before this.
  if (workspacesSuccess && hydratedWorkspaces.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled className="text-muted-foreground">
            <div className="bg-muted flex aspect-square size-8 items-center justify-center rounded-lg">
              <Building2 className="size-4" />
            </div>
            <div className="grid flex-1 text-start text-sm leading-tight">
              <span className="truncate font-medium">{t('shell.teamSwitcher.noWorkspaces')}</span>
              <span className="truncate text-xs">{t('shell.teamSwitcher.contactAdmin')}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  // Nothing to render yet (pre-hydration / activeWorkspace not resolved).
  if (!activeWorkspace) {
    return null
  }

  // Progressive complexity: hide switcher when only 1 workspace
  if (!showSelector()) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-default">
            <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
              <Building2 className="size-4" />
            </div>
            <div className="grid flex-1 text-start text-sm leading-tight">
              <UserText truncate className="font-medium">{activeWorkspace.name}</UserText>
              <UserText truncate className="text-xs">{activeWorkspace.companyName}</UserText>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className={`flex aspect-square size-8 items-center justify-center rounded-lg ${isAllView ? 'bg-primary/10 text-primary' : 'bg-sidebar-primary text-sidebar-primary-foreground'}`}>
                {isAllView ? <Globe className="size-4" /> : <Building2 className="size-4" />}
              </div>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <UserText truncate className="font-medium">{activeWorkspace.name}</UserText>
                <UserText truncate className="text-xs">{activeWorkspace.companyName}</UserText>
              </div>
              <ChevronsUpDown className="ms-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            {/* All Workspaces aggregate view */}
            <DropdownMenuItem
              onClick={() => handleSwitch('all')}
              className="gap-2 p-2"
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-primary/10">
                <Globe className="size-3.5 shrink-0 text-primary" />
              </div>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-medium">{t('nav.allWorkspaces')}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {t('nav.readOnlyAggregate')}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t('nav.workspaces')}
            </DropdownMenuLabel>
            {hydratedWorkspaces.map((workspace, index) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => handleSwitch(workspace.id)}
                className="gap-2 p-2"
              >
                <div className="flex size-6 items-center justify-center rounded-md border">
                  <Building2 className="size-3.5 shrink-0" />
                </div>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <UserText truncate className="font-medium">{workspace.name}</UserText>
                  <UserText truncate className="text-xs text-muted-foreground">
                    {workspace.companyName}
                  </UserText>
                </div>
                <DropdownMenuShortcut>{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
